import { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { useLazyGetActivityLogsListQuery } from '@src/services/activityLogs/activityLogsList/activityLogsListApiSlice';
import { FilterFormValue, normalizeFilters } from '@src/utilities/form';
import { SortDirection } from '@src/types/common';
import { LIST_LIMIT } from '@src/constants/pagination';

export const useLogsList = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(LIST_LIMIT);
    const [searchText, setSearchText] = useState('');
    const [filters, setFilters] = useState<Record<string, FilterFormValue>>();
    const [openFilter, setOpenFilter] = useState(false);
    const [showLocalTime, setShowLocalTime] = useState(false);
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<SortDirection | null>(null);

    const [getLogs, { data, isLoading, isFetching, isError }] =
        useLazyGetActivityLogsListQuery();

    const fetchLogs = useCallback(
        _.debounce((params: any) => {
            getLogs(params);
        }, 500),
        [getLogs]
    );

    useEffect(() => {
        const trimmedSearch = searchText?.trim();
        if (trimmedSearch && trimmedSearch.length < 3) return;

        fetchLogs({
            page,
            limit: pageSize,
            search: searchText || undefined,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
            ...normalizeFilters(filters),
        });
    }, [searchText, page, pageSize, filters, sortBy, sortOrder]);

    const handleChangePagination = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchText(val);
        setPage(1);
    };

    const handleFilterChange = (key: string, value: FilterFormValue) => {
        setPage(1);

        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleRemoveAllFilters = () => {
        setFilters({});
        setPage(1);
    };

    const handleActionClick = (action: 'openFilter') => {
        switch (action) {
            case 'openFilter':
                setOpenFilter(true);
                break;
        }
    };

    const handleSortChange = (
        columnKey: string,
        order: SortDirection | null
    ) => {
        if (order === null) {
            setSortBy(null);
            setSortOrder(null);
        } else {
            setSortBy(columnKey);
            setSortOrder(order);
        }
        setPage(1);
    };

    return {
        page,
        pageSize,
        data,
        isLoading,
        isFetching,
        filters,
        searchText,
        isError,
        openFilter,
        showLocalTime,
        sortBy,
        sortOrder,
        handleActionClick,
        setOpenFilter,
        handleSearchChange,
        handleFilterChange,
        handleRemoveAllFilters,
        handleChangePagination,
        setShowLocalTime,
        handleSortChange,
    };
};
